import React, { useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import toast from "react-hot-toast";
import { signIn, generateOtp } from "../../services/authServices";
import { getDeviceInfo } from "../../utils/deviceInfo";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLogId } from "../../actions/actions";
import logger from "../../utils/logger";
import "./onboarding.css";

interface MpinLoginProps {
  onBackToPhone: () => void;
  onMpinNotSet: (phone: string, countryCode: string) => void;
}

const MpinLogin: React.FC<MpinLoginProps> = ({ onBackToPhone, onMpinNotSet }) => {
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>("91");
  const [mpin, setMpin] = useState<string>("");
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPin, setShowPin] = useState<boolean>(false);
  const [attemptCount, setAttemptCount] = useState<number>(0);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [blockTime, setBlockTime] = useState<number>(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Clear PIN setup flag when component mounts (user navigated away)
  useEffect(() => {
    return () => {
      // Only clear flag if we're not in the middle of a PIN setup flow
      const mpinSetupType = localStorage.getItem("mpinSetupType");
      if (localStorage.getItem("showMpinSetup") && !mpinSetupType) {
        localStorage.removeItem("showMpinSetup");
      }
      // Clear sensitive data
      setMpin("");
      setShowPin(false);
    };
  }, []);

  // Clear PIN input when component unmounts or on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setShowPin(false); // Hide PIN when tab becomes inactive
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const validatePhoneNumber = (number: string): boolean => {
    const phoneNumberPattern = /^[1-9]\d{9}$/;
    return phoneNumberPattern.test(number);
  };

  const handlePhoneChange = (value: string, country: any) => {
    const newCountryCode = country.dialCode;
    const numberWithoutCode = value.slice(newCountryCode.length);

    setPhoneNumber(numberWithoutCode);
    setCountryCode(newCountryCode);
    setIsValid(validatePhoneNumber(numberWithoutCode));
  };

  const handleMpinChange = (value: string) => {
    // Only allow numeric input and limit to 4 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 4);
    setMpin(numericValue);
  };

  // Rate limiting function - VERY GENEROUS FOR TESTING
  const checkRateLimit = () => {
    const key = `mpin_attempts_${phoneNumber}`;
    const attempts = JSON.parse(localStorage.getItem(key) || '[]');
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes instead of 1 hour
    
    // Filter attempts from last 5 minutes
    const recentAttempts = attempts.filter((time: number) => now - time < fiveMinutes);
    
    // Allow 20 attempts in 5 minutes (very generous)
    if (recentAttempts.length >= 20) {
      setIsBlocked(true);
      const oldestAttempt = Math.min(...recentAttempts);
      setBlockTime(Math.ceil((fiveMinutes - (now - oldestAttempt)) / 60000)); // minutes
      return false;
    }
    
    return true;
  };

  const recordAttempt = () => {
    const key = `mpin_attempts_${phoneNumber}`;
    const attempts = JSON.parse(localStorage.getItem(key) || '[]');
    attempts.push(Date.now());
    localStorage.setItem(key, JSON.stringify(attempts));
    setAttemptCount(attempts.length);
  };

  // Helper function to clear rate limit (for testing)
  const clearRateLimit = () => {
    const key = `mpin_attempts_${phoneNumber}`;
    localStorage.removeItem(key);
    setAttemptCount(0);
    setIsBlocked(false);
    setBlockTime(0);
    toast.success("Rate limit cleared!");
  };

  const handlePinSetupFlow = async (isReset = false) => {
    // Validate phone number first
    if (!phoneNumber || !validatePhoneNumber(phoneNumber)) {
      toast.error("Please enter a valid phone number first");
      return;
    }

    logger.log("[MPIN Login] Initiating PIN setup flow:", {
      setupType: isReset ? 'reset' : 'setup',
      phone: phoneNumber,
      countryCode: countryCode
    });

    try {
      // Generate OTP for the phone number
      const response = await generateOtp({
        phone: phoneNumber,
        countryCode: countryCode,
        otpType: "custom_sms_otp",
      });

      if (response && response.status) {
        // Store phone details and dispatch logId
        localStorage.setItem("phone", phoneNumber);
        localStorage.setItem("countryCode", countryCode);
        localStorage.setItem("showMpinSetup", "true");
        localStorage.setItem("mpinSetupType", isReset ? "reset" : "setup");
        dispatch(setLogId(response?.data?.data?.LogID));

        logger.log("[MPIN Login] Setup flags stored, navigating to OTP verification");
        toast.success(`OTP sent! Please verify to ${isReset ? 'reset' : 'set up'} your PIN.`);
        navigate("/otp-verify");
      }
    } catch (error) {
      logger.error("[MPIN Login] Error generating OTP:", error);
      toast.error("Failed to send OTP. Please try again.");
      // Fallback to manual phone entry
      onMpinNotSet(phoneNumber, countryCode);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePhoneNumber(phoneNumber)) {
      setIsValid(false);
      return;
    }

    if (mpin.length !== 4) {
      toast.error("Please enter your 4-digit PIN");
      return;
    }

    // Check rate limiting
    if (!checkRateLimit()) {
      toast.error(`Too many attempts. Please wait ${blockTime} minutes before trying again.`);
      return;
    }

    if (isBlocked) {
      toast.error(`Account temporarily blocked. Please wait ${blockTime} minutes.`);
      return;
    }

    setIsLoading(true);
    recordAttempt();

    try {
      const data = {
        loginType: "mpin",
        loggedFrom: "web",
        deviceToken: JSON.parse(localStorage.getItem('fcm_token_data') || '{}').token,
        countryCode: countryCode,
        phone: phoneNumber,
        mpin: mpin,
        deviceInfo: await getDeviceInfo(),
      };

      const resp = await signIn(data);
      
      if (resp && resp.status) {
        localStorage.setItem("auth_token", resp?.data?.data?.token);
        localStorage.setItem("userData", JSON.stringify(resp?.data?.data));

        // 🏢 STORE BUSINESS DATA - same as OTP login
        if (resp?.data?.data?.multiAccountList?.length) {
          logger.log('🏢 [MPIN] Storing userBusinesses - Count:', resp.data.data.multiAccountList.length);
          logger.log('🏢 [MPIN] Business list:', resp.data.data.multiAccountList);
          localStorage.setItem(
            "userBusinesses",
            JSON.stringify(resp.data.data.multiAccountList)
          );
          logger.log('🏢 [MPIN] Stored in localStorage - userBusinesses set');
        } else {
          logger.warn('🏢 [MPIN] No multiAccountList found or empty array');
        }

        // Store user preferences if available
        if (resp.data.data.userPreference) {
          localStorage.setItem(
            "user_preferences",
            JSON.stringify(resp.data.data.userPreference)
          );
        }

        logger.log("🔑 MPIN LOGIN - Data stored:", {
          timestamp: new Date().toISOString(),
          loginType: 'mpin',
          userData: resp?.data?.data,
          hasUserPreference: !!resp?.data?.data?.userPreference,
          businessCount: resp?.data?.data?.multiAccountList?.length || 0,
          userDetailsType: Array.isArray(resp?.data?.data) ? 'ARRAY' : 'OBJECT'
        });

        if (resp?.data?.data?.isOnboardCompleted) {
          if (resp?.data?.data?.isSubscriptionActive) {
            toast.success("Logged In Successfully!");
            navigate("/leads", { replace: true });
          } else {
            navigate("/SubscriptionExpiredPage", { replace: true });
          }
        } else {
          navigate("./sign-up");
        }
      }
    } catch (error: any) {
      logger.error("MPIN Login Error:", error);
      
      // Extract specific error message from API response
      const errorMessage = error.response?.data?.message || "Login failed. Please try again.";
      const statusCode = error.response?.status;
      
      logger.log("🔴 MPIN Login Error Details:", {
        statusCode,
        errorMessage,
        fullResponse: error.response?.data
      });
      
      // Check if error indicates MPIN not set or invalid MPIN
      if (statusCode === 422) {
        // API returned 422 - check if it's "not set" or "invalid"
        const isNotSet = errorMessage.toLowerCase().includes('not set') || 
                        errorMessage.toLowerCase().includes('not found') ||
                        errorMessage.toLowerCase().includes('no mpin') ||
                        errorMessage.toLowerCase().includes('mpin not');
        
        if (isNotSet) {
          // MPIN not set - redirect to setup flow
          toast.error("PIN not set. Sending OTP to set it up...");
          await handlePinSetupFlow(false);
        } else {
          // Invalid MPIN - show error
          toast.error(`${errorMessage}`);
        }
      } else if (statusCode === 400 || statusCode === 404) {
        // User hasn't set MPIN yet, generate OTP and redirect
        toast.error("PIN not set. Sending OTP to set it up...");
        await handlePinSetupFlow(false);
      } else if (statusCode === 401) {
        toast.error(errorMessage || "Invalid MPIN. Please try again.");
      } else if (statusCode === 403 && 
                 error.response.data?.data?.isFreePlan) {
        navigate("/SubscriptionExpiredPage", { replace: true });
      } else {
        // Show specific API error message or fallback
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        {/* Logo */}
        <div className="onboarding-logo">
          <img src="assets/img/3sigma_logo.png" alt="3Sigma CRM" />
        </div>

        {/* Header */}
        <div className="onboarding-header">
          <h2>Quick PIN Login</h2>
          <p className="onboarding-subtitle">
            Enter your phone number and 4-digit PIN
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="onboarding-form">
          <div className="onboarding-form-group">
            <label>Phone Number</label>
            <div className="onboarding-phone-container">
              <PhoneInput
                country="in"
                value={`${countryCode}${phoneNumber}`}
                onChange={handlePhoneChange}
                inputClass={!isValid ? "is-invalid" : ""}
                containerClass="react-tel-input"
              />
              {!isValid && (
                <span className="onboarding-error-text">
                  Please enter a valid mobile number
                </span>
              )}
            </div>
          </div>

          <div className="onboarding-form-group">
            <label>4-digit PIN</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPin ? "text" : "password"}
                className="onboarding-input"
                value={mpin}
                onChange={(e) => handleMpinChange(e.target.value)}
                placeholder="••••"
                maxLength={4}
                autoComplete="off"
                spellCheck="false"
                autoCapitalize="off"
                autoCorrect="off"
                inputMode="numeric"
                pattern="[0-9]*"
                style={{
                  textAlign: "center",
                  letterSpacing: "12px",
                  fontSize: "20px",
                  fontWeight: "700",
                  paddingRight: "45px",
                  border: isBlocked ? "2px solid #ef4444" : undefined,
                }}
                disabled={isBlocked || isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "18px",
                  padding: "4px",
                }}
              >
                {showPin ? "🙈" : "👁️"}
              </button>
            </div>

            {isBlocked && (
              <div style={{ marginTop: "12px", textAlign: "center" }}>
                <span className="onboarding-error-text" style={{ display: "block", marginBottom: "8px" }}>
                  🔒 Too many failed attempts. Please wait {blockTime} minutes.
                </span>
                <button
                  type="button"
                  onClick={clearRateLimit}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#0056fe",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  🔧 Clear Rate Limit (Testing)
                </button>
              </div>
            )}
          </div>

          <button
            type="submit"
            className="onboarding-btn-primary"
            disabled={isLoading || !isValid || mpin.length !== 4 || isBlocked}
          >
            {isLoading ? (
              <span className="onboarding-loading">
                <span className="onboarding-spinner"></span>
                Logging In...
              </span>
            ) : isBlocked ? (
              "Account Blocked"
            ) : (
              "Login"
            )}
          </button>

          {/* Action Links */}
          <div style={{ marginTop: "24px", textAlign: "center" }}>
            <button
              type="button"
              onClick={onBackToPhone}
              style={{
                background: "none",
                border: "none",
                color: "#0056fe",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                marginBottom: "12px",
              }}
            >
              ← Back to Phone Login
            </button>
            <div style={{ display: "flex", justifyContent: "center", gap: "8px", fontSize: "14px" }}>
              <button
                type="button"
                onClick={() => handlePinSetupFlow(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#0056fe",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Setup PIN
              </button>
              <span style={{ color: "#cbd5e1" }}>•</span>
              <button
                type="button"
                onClick={() => handlePinSetupFlow(true)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#0056fe",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Reset PIN
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MpinLogin;